import React, { useState } from 'react';
import { useQuery, useMutation } from '@apollo/client';
import { useDispatch } from 'react-redux';
import { openHeaderModal } from '../../actions/siteadmin/modalActions';
import myCommunityDiscountQuery from './myCommunityDiscount.graphql';
import applyCommunityDiscountMutation from './applyCommunityDiscount.graphql';

const RED = '#D32F2F';
const TEAL = '#0A7C70';
const INK = '#1F2733';
const LINE = '#E6E8EC';

function CommunityDiscount({ isAuthenticated }) {
  const dispatch = useDispatch();
  const { data, loading, refetch } = useQuery(myCommunityDiscountQuery, { fetchPolicy: 'network-only', skip: !isAuthenticated });
  const [apply, { loading: submitting }] = useMutation(applyCommunityDiscountMutation);
  const [type, setType] = useState(null);
  const [proof, setProof] = useState(null);
  const [uploading, setUploading] = useState(false);

  const d = (data && data.myCommunityDiscount) || {};
  const percent = d.percent || 50;
  const status = d.result && d.result.status;

  const onPick = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const resp = await fetch('/uploadCompanyDocument', { method: 'POST', body: fd, credentials: 'same-origin' });
      const json = await resp.json();
      if (json && json.status === 200 && json.documentUrl) setProof(json.documentUrl);
      else alert('Upload failed. Please try again.');
    } catch (err) { alert('Upload failed. Please try again.'); }
    setUploading(false);
  };

  const onSubmit = async () => {
    if (!type) return alert('Choose Senior or Veteran first.');
    if (!proof) return alert('Please upload a photo of your proof.');
    await apply({ variables: { type, proofDocument: proof } });
    refetch();
  };

  const card = (title, body, color) => (
    <div style={{ padding: 18, borderRadius: 14, background: `${color}14`, border: `1px solid ${color}66`, marginBottom: 18 }}>
      <div style={{ fontWeight: 800, fontSize: 16, color }}>{title}</div>
      <div style={{ marginTop: 6, color: INK, lineHeight: 1.5 }}>{body}</div>
    </div>
  );

  const typeTile = (val, label, sub) => {
    const sel = type === val;
    return (
      <button type="button" onClick={() => setType(val)} style={{ display: 'block', width: '100%', textAlign: 'left', marginBottom: 10, padding: 16, borderRadius: 12, cursor: 'pointer', background: sel ? `${RED}0F` : '#fff', border: `${sel ? 2 : 1}px solid ${sel ? RED : LINE}` }}>
        <div style={{ fontWeight: 700, color: INK }}>{label}</div>
        <div style={{ fontSize: 13, color: '#5B6472' }}>{sub}</div>
      </button>
    );
  };

  return (
    <div style={{ maxWidth: 620, margin: '0 auto', padding: '24px 18px', fontFamily: 'inherit', color: INK }}>
      <h1 style={{ fontSize: 26, fontWeight: 800, margin: '0 0 8px' }}>Senior &amp; Veteran Discount</h1>
      <p style={{ color: '#5B6472', lineHeight: 1.5, margin: '0 0 20px' }}>
        Seniors (65+) and U.S. veterans get <b>{percent}% off the labour</b> on small jobs.
        Apply with proof — our team reviews each application.
      </p>

      {!isAuthenticated ? (
        <>
          {/* How to qualify (public) */}
          <div style={{ fontWeight: 800, marginBottom: 10 }}>How to qualify</div>
          {typeTile('senior', 'Senior (65+)', 'Proof: government ID showing your date of birth')}
          {typeTile('veteran', 'U.S. Veteran', 'Proof: VA ID card, DD-214, or veteran designation')}
          <div style={{ marginTop: 18 }}>
            <button type="button" onClick={() => dispatch(openHeaderModal('loginModal'))} style={{ background: RED, color: '#fff', border: 'none', borderRadius: 12, padding: '14px 26px', fontWeight: 800, fontSize: 15, cursor: 'pointer' }}>Log in or sign up to apply →</button>
          </div>
          <p style={{ color: '#9AA4AF', fontSize: 12, marginTop: 12 }}>You need a BestHand account to apply. It takes a minute — then upload your proof and we review it.</p>
        </>
      ) : loading ? <div>Loading…</div> : (
        <>
          {status === 'approved' && card('🎉 You\'re approved', `Your ${percent}% discount is applied automatically to eligible small jobs.${d.result.expiresAt ? ` Valid until ${String(d.result.expiresAt).slice(0, 10)}.` : ''}`, TEAL)}
          {status === 'pending' && card('⏳ Under review', 'We received your application and are reviewing your proof. You\'ll be notified once it\'s approved.', INK)}
          {(status == null || status === 'rejected') && (
            <>
              {status === 'rejected' && card('Application not approved', d.result.rejectionReason || 'Please re-apply with valid proof.', RED)}
              <div style={{ fontWeight: 800, marginBottom: 10 }}>I am a…</div>
              {typeTile('senior', 'Senior (65+)', 'Proof: government ID showing your date of birth')}
              {typeTile('veteran', 'U.S. Veteran', 'Proof: VA ID card, DD-214, or veteran designation')}
              <label style={{ display: 'inline-block', marginTop: 6, marginBottom: 18, padding: '12px 18px', borderRadius: 10, border: `1px solid ${proof ? TEAL : RED}`, color: proof ? TEAL : RED, fontWeight: 700, cursor: 'pointer' }}>
                {uploading ? 'Uploading…' : (proof ? '✓ Proof uploaded' : 'Upload proof')}
                <input type="file" accept="image/*,application/pdf" onChange={onPick} style={{ display: 'none' }} />
              </label>
              <div>
                <button type="button" onClick={onSubmit} disabled={submitting} style={{ background: RED, color: '#fff', border: 'none', borderRadius: 12, padding: '14px 26px', fontWeight: 800, fontSize: 15, cursor: 'pointer' }}>
                  {submitting ? 'Submitting…' : 'Submit application'}
                </button>
              </div>
              <p style={{ color: '#9AA4AF', fontSize: 12, marginTop: 12 }}>Your document is used only to verify eligibility.</p>
            </>
          )}
        </>
      )}
    </div>
  );
}

export default CommunityDiscount;
